import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSite } from '../../contexts/SiteContext';
import StatCard from '../../components/charts/StatCard';
import BarChart from '../../components/charts/BarChart';
import DataTable from '../../components/tables/DataTable';
import ViewTicketModal from '../../components/features/tickets/ViewTicketModal';
import customerService from '../../services/customerService';
import { formatDateTime } from '../../utils/dateFormatter';
import { STATUS_COLORS, PRIORITY_COLORS } from '../../constants/statuses';

const Overview = ({navigateToTickets}) => {
  const { user } = useAuth();
  const { selectedSite } = useSite();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    if (selectedSite !== null) {
      loadDashboardData();
    }
  }, [selectedSite]);

  const loadDashboardData = async () => {
    setLoading(true);
    const result = await customerService.getDashboardData(user.id, selectedSite);
    if (result.success) {
      setDashboardData(result.data);
    }
    setLoading(false);
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedTicket(null);
  };

  const statBarStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px 24px',
    marginBottom: '24px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '24px'
  };

  const statItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  };

  const statLabelStyle = {
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const statValueStyle = {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--text-primary)'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '24px'
  };

  const chartsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '20px',
    marginBottom: '24px'
  };

  const sectionTitleStyle = {
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    marginBottom: '16px'
  };

  const columns = [
    {
      key: 'id',
      label: 'Ticket ID',
      render: (value) => `#${value}`
    },
    {
      key: 'title',
      label: 'Title'
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${PRIORITY_COLORS[value] || '#6b7280'}20`,
          color: PRIORITY_COLORS[value] || '#6b7280'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${STATUS_COLORS[value] || '#6b7280'}20`,
          color: STATUS_COLORS[value] || '#6b7280'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value) => formatDateTime(value)
    },
    {
      key: 'site_name',
      label: 'Site',
      condition: () => selectedSite === 0
    }
  ];

  if (loading || selectedSite === null) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading dashboard...
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        No data available
      </div>
    );
  }

  const { site, stats, charts, recentTickets } = dashboardData;

  const getContractTypeLabel = (type) => {
    if (type === 'CAMC') return 'Comprehensive';
    if (type === 'NCAMC') return 'Non-Comprehensive';
    return type || 'N/A';
  };

  return (
    <div>
      <div style={statBarStyle}>
        <div style={statItemStyle}>
          <div style={statLabelStyle}>Location</div>
          <div style={statValueStyle}>{site.site_name}</div>
        </div>
        <div style={statItemStyle}>
          <div style={statLabelStyle}>Contact Person</div>
          <div style={statValueStyle}>{site.contact_person}</div>
        </div>
        <div style={statItemStyle}>
          <div style={statLabelStyle}>Phone</div>
          <div style={statValueStyle}>{site.contact_phone}</div>
        </div>
        <div style={statItemStyle}>
          <div style={statLabelStyle}>Email</div>
          <div style={statValueStyle}>{site.contact_email}</div>
        </div>
      </div>

      {selectedSite !== 0 && (
        <div style={{ 
          background: 'var(--surface)', 
          border: '1px solid var(--border)', 
          borderRadius: '12px', 
          padding: '24px', 
          marginBottom: '24px' 
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <h4 style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.05em' }}>
                Contract Coverage for {site.site_name}
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: 'var(--text-primary)' }}>
                <span><strong>PO:</strong> {stats.amcStatus.po_number || 'N/A'}</span>
                <span><strong>Validity:</strong> {stats.amcStatus.start_date ? new Date(stats.amcStatus.start_date).toLocaleDateString() : 'N/A'} - {stats.amcStatus.end_date ? new Date(stats.amcStatus.end_date).toLocaleDateString() : 'N/A'}</span>
                {stats.amcStatus.status !== 'Active' && (
                  <span style={{ 
                    padding: '2px 8px', 
                    borderRadius: '4px', 
                    background: `${stats.amcStatus.color}20`, 
                    color: stats.amcStatus.color, 
                    fontSize: '11px', 
                    fontWeight: '700',
                    textTransform: 'uppercase'
                  }}>
                    {stats.amcStatus.status}
                  </span>
                )}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
               <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '4px' }}>AMC Type</div>
               <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--primary)' }}>{getContractTypeLabel(stats.amcStatus.type)}</div>
            </div>
          </div>

          {!stats.amcStatus.services ? (
            <div style={{ 
              padding: '20px', 
              background: 'var(--surface-light)', 
              borderRadius: '8px', 
              textAlign: 'center', 
              color: 'var(--text-secondary)',
              fontSize: '14px',
              border: '1px dashed var(--border)'
            }}>
              No active contract found for this location. Services may be limited to on-call requests.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
              {stats.amcStatus.services.comprehensive?.length > 0 && (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--success)', marginBottom: '8px' }}>Comprehensive</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {stats.amcStatus.services.comprehensive.map(s => (
                      <span key={s} style={{ fontSize: '12px', background: 'var(--success-light)', color: 'var(--success)', padding: '4px 8px', borderRadius: '4px' }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {stats.amcStatus.services.non_comprehensive?.length > 0 && (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', marginBottom: '8px' }}>Non-Comprehensive</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {stats.amcStatus.services.non_comprehensive.map(s => (
                      <span key={s} style={{ fontSize: '12px', background: 'var(--info-light)', color: 'var(--info)', padding: '4px 8px', borderRadius: '4px' }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div style={gridStyle}>
        <StatCard
          title="Total Tickets"
          value={stats.total}
          color="#3b82f6"
          onClick={() => navigateToTickets('')}
        />
        <StatCard
          title="Open Tickets"
          value={stats.open}
          color="#f59e0b"
          onClick={() => navigateToTickets('OPEN')}
        />
        <StatCard
          title="Completed Tickets"
          value={stats.resolved}
          color="#10b981"
          onClick={() => navigateToTickets('CLOSED')}
        />
        <StatCard
          title="AMC Status"
          value={stats.amcStatus.status}
          subtitle={stats.amcStatus.message}
          color={stats.amcStatus.color}
        />
      </div>

      <div style={chartsGridStyle}>
        <BarChart
          data={charts.history}
          title="Tickets History (Last 6 Months)"
          xKey="month"
          yKey="count"
          color="var(--primary)"
        />
        <BarChart
          data={charts.byPriority}
          title="Tickets by Priority"
          xKey="priority"
          yKey="count"
          color="#f59e0b"
        />
      </div>

      <div>
        <div style={sectionTitleStyle}>Recent Tickets - {site.site_name}</div>
        <DataTable
          data={recentTickets}
          columns={columns.filter(col => typeof col.condition === 'function' ? col.condition() : true)}
          onRowClick={handleViewTicket}
        />
      </div>

      {showViewModal && selectedTicket && (
        <ViewTicketModal
          isOpen={showViewModal}
          onClose={handleCloseViewModal}
          ticket={selectedTicket}
        />
      )}
    </div>
  );
};

export default Overview;